import { snakeToCamel } from '@eventbrite/string-utils';
import { transformUtil } from '@eventbrite/transformation-utils';
import omit from 'lodash/omit';
import { arrayOf, Schema } from 'normalizr';
import {
    SEARCH_CRITERIA_TYPE_MAP,
    SEARCH_TYPE_MAP,
    SINGULARIZED_ENTITY_TYPE_MAP,
} from '../constants';
import { ONLINE_BANNER_URL } from '../constants/constants';
import { Branding } from '../types/index';

export const getTransformedContent = (key: any, response: any) => {
    /* Due to how the normalizr library is handling this instantiation
   server side, necessary to do an additional interpolation step. */
    const content = new Schema(`${key}`);

    return transformUtil({
        response,
        schema: {
            [key]: arrayOf(content),
        },
    });
};

const _addSearchQueryToResults = (
    results: Record<string, string[]>,
    searchData: { [index: string]: object },
    type: string,
) => ({
    ...results,
    searchQuery: searchData[`${type}_search`],
});

/*
 * Building the pagination in an abstract way, so that
 * we will be able to start handling profiles/articles.
 * But don't include unless the ids for the respective types exist
 *
 * Output:
 *  pagination: {
 *    event: [
 *        ids: [1,2,3,4,5],
 *        object_count: 1234,
 *        page_size: 10,
 *        continuation: asdfasdf
 *   ],
 *   profile: [],
 *   article: [],
 * }
 */
const _constructPagination = (
    paginationData: any,
    pagination: any,
    ids: any,
    type: any,
) => {
    let constructedPagination = paginationData;

    if (ids && pagination) {
        let pageNumberKey = pagination.pageNumber - 1;

        if (!Number.isInteger(pageNumberKey)) {
            // Some API responses do not have a page number, e.g. articles
            pageNumberKey = 0;
        }

        constructedPagination = {
            ...paginationData,
            currentPage: pageNumberKey,
            [type]: {
                [pageNumberKey]: {
                    ...pagination,
                    ids,
                },
            },
        };
    }

    return constructedPagination;
};

const _parseSearchCriterias = (searchData: any) =>
    SEARCH_CRITERIA_TYPE_MAP.reduce((memo: any, key: any) => {
        const schema = new Schema('results');

        const transformedContent = transformUtil({
            response: searchData[key],
            schema,
        });

        return {
            ...memo,
            [snakeToCamel(key)]: omit(
                transformedContent.entities.results[transformedContent.result],
                'pointRadius',
            ),
        };
    }, {});

export const transformSearchBucketContent = (searchData: any) => {
    let entities = {};
    let paginationData = {};
    let entityContext = [];
    let searchCriterias = {};

    if (searchData) {
        entityContext = SEARCH_TYPE_MAP.map((key: any) => {
            const type = SINGULARIZED_ENTITY_TYPE_MAP[key];

            let {
                result: { results: ids, pagination, ...results },
                entities: { results: newEntities },
            } = getTransformedContent('results', searchData[key]);

            results = _addSearchQueryToResults(results, searchData, type);
            paginationData = _constructPagination(
                paginationData,
                pagination,
                ids,
                type,
            );

            entities = { ...entities, ...newEntities };

            if (searchData[key].top_match) {
                const {
                    result: { topMatch: topMatchIds },
                    entities: { top_match: topMatchEntities },
                } = getTransformedContent('top_match', searchData[key]);

                entities = { ...entities, ...topMatchEntities };

                return { type, ids, topMatchIds, ...results };
            }

            return { type, ids, ...results };
        });

        searchCriterias = {
            ..._parseSearchCriterias(searchData),
            searchId: searchData['search_id'],
        };
    }

    return {
        entities,
        entityContext,
        searchCriterias,
        pagination: paginationData,
    };
};

export const transformBrandingResponse = (
    branding: Branding,
    isOnline?: boolean,
) => {
    const {
        attribution: {
            name: artistName,
            summary: artistSummary,
            image: { original: { url: artistImageUrl } = {} as any } = {},
            social_links: artistSocialLinks,
        } = {} as any,
        header: {
            image: {
                original: { url: headerImageUrl } = {} as any,
                url: headerSmallImageUrl,
            } = {} as any,
        } = {},
        middle: {
            image: {
                original: { url: middleImageUrl } = {} as any,
                url: middleSmallImageUrl,
            } = {} as any,
        } = {},
        footer: {
            image: {
                original: { url: footerImageUrl } = {} as any,
                url: footerSmallImageUrl,
            } = {} as any,
        } = {},
        decoration_top_left: {
            image: { original: { url: decorationTopLeftUrl } = {} as any } = {},
        } = {},
        decoration_top_right: {
            image: {
                original: { url: decorationTopRightUrl } = {} as any,
            } = {},
        } = {},
        decoration_bottom_left: {
            image: {
                original: { url: decorationBottomLeftUrl } = {} as any,
            } = {},
        } = {},
        decoration_bottom_right: {
            image: {
                original: { url: decorationBottomRightUrl } = {} as any,
            } = {},
        } = {},
    } = branding;

    let onlineHeaderImage = null;

    if (isOnline) {
        onlineHeaderImage = ONLINE_BANNER_URL;
    }

    return {
        artistName,
        artistSummary,
        artistImageUrl,
        headerImageUrl: onlineHeaderImage || headerImageUrl,
        headerSmallImageUrl: onlineHeaderImage || headerSmallImageUrl,
        middleImageUrl,
        middleSmallImageUrl,
        footerImageUrl,
        footerSmallImageUrl,
        decorationTopLeftUrl,
        decorationTopRightUrl,
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
        artistSocialLinks,
    };
};
